/**
 * 曲线采样纯数学工具。
 *
 * 设计说明：
 * 1. 该模块只负责参数曲线的纯数学采样，不依赖 Puppy CAD 内核、three.js 或渲染状态；
 * 2. 草图曲线预览（拖拽期 CPU 轻量采样）、profile 特征网格和 Worker 曲线计算命令必须复用同一套算法；
 * 3. Puppy CAD 内核 插值样条仍由调用方处理，本模块只覆盖 Bezier / BSpline / rational 变体。
 */

/** 三维曲线采样点。 */
export interface CurveSamplePoint3 {
    /** X 坐标。 */
    x: number;
    /** Y 坐标。 */
    y: number;
    /** Z 坐标。 */
    z: number;
}

/**
 * 构造开放均匀 B 样条节点向量。
 * @param pointCount 控制点数量。
 * @param degree 曲线次数。
 * @returns 节点向量。
 */
export function buildClampedUniformKnots(pointCount: number, degree: number): number[] {
    const lastControlPointIndex = pointCount - 1;
    const resolvedDegree = Math.max(1, Math.min(degree, lastControlPointIndex));
    const knotLastIndex = lastControlPointIndex + resolvedDegree + 1;
    const knots: number[] = new Array(knotLastIndex + 1).fill(0);

    for (let index = 0; index <= resolvedDegree; index += 1) {
        knots[index] = 0;
    }
    for (let index = knotLastIndex - resolvedDegree; index <= knotLastIndex; index += 1) {
        knots[index] = 1;
    }

    const interiorCount = lastControlPointIndex - resolvedDegree;
    if (interiorCount > 0) {
        for (let index = 1; index <= interiorCount; index += 1) {
            knots[resolvedDegree + index] = index / (interiorCount + 1);
        }
    }

    return knots;
}

/**
 * 查找 B 样条参数所在的节点区间。
 * @param lastControlPointIndex 控制点末索引。
 * @param degree 曲线次数。
 * @param parameter 曲线参数。
 * @param knots 节点向量。
 * @returns 节点区间索引。
 */
function findKnotSpan(
    lastControlPointIndex: number,
    degree: number,
    parameter: number,
    knots: number[],
): number {
    if (parameter >= knots[lastControlPointIndex + 1]!) {
        return lastControlPointIndex;
    }
    if (parameter <= knots[degree]!) {
        return degree;
    }

    let low = degree;
    let high = lastControlPointIndex + 1;
    let middle = Math.floor((low + high) / 2);
    while (parameter < knots[middle]! || parameter >= knots[middle + 1]!) {
        if (parameter < knots[middle]!) {
            high = middle;
        } else {
            low = middle;
        }
        middle = Math.floor((low + high) / 2);
    }
    return middle;
}

/**
 * 使用 De Boor 算法计算有理 B 样条曲线点。
 * @param parameter 曲线参数。
 * @param degree 曲线次数。
 * @param controlPoints 控制点。
 * @param weights 控制点权重。
 * @param knots 节点向量。
 * @returns 曲线点。
 */
function evaluateRationalBSplinePoint(
    parameter: number,
    degree: number,
    controlPoints: CurveSamplePoint3[],
    weights: number[],
    knots: number[],
): CurveSamplePoint3 {
    const lastControlPointIndex = controlPoints.length - 1;
    const resolvedDegree = Math.max(1, Math.min(degree, lastControlPointIndex));
    const span = findKnotSpan(lastControlPointIndex, resolvedDegree, parameter, knots);
    const workingPoints: Array<{ x: number; y: number; z: number; w: number }> = [];

    for (let index = 0; index <= resolvedDegree; index += 1) {
        const controlPointIndex = span - resolvedDegree + index;
        const point = controlPoints[controlPointIndex]!;
        const weight = Math.max(1e-6, weights[controlPointIndex] ?? 1);
        workingPoints[index] = {
            x: point.x * weight,
            y: point.y * weight,
            z: point.z * weight,
            w: weight,
        };
    }

    for (let step = 1; step <= resolvedDegree; step += 1) {
        for (let index = resolvedDegree; index >= step; index -= 1) {
            const knotIndex = span - resolvedDegree + index;
            const denominator = knots[knotIndex + resolvedDegree - step + 1]! - knots[knotIndex]!;
            const alpha = denominator === 0 ? 0 : (parameter - knots[knotIndex]!) / denominator;
            const previous = workingPoints[index - 1]!;
            const current = workingPoints[index]!;
            workingPoints[index] = {
                x: (1 - alpha) * previous.x + alpha * current.x,
                y: (1 - alpha) * previous.y + alpha * current.y,
                z: (1 - alpha) * previous.z + alpha * current.z,
                w: (1 - alpha) * previous.w + alpha * current.w,
            };
        }
    }

    const result = workingPoints[resolvedDegree]!;
    return result.w === 0
        ? { x: result.x, y: result.y, z: result.z }
        : { x: result.x / result.w, y: result.y / result.w, z: result.z / result.w };
}

/**
 * 归一化控制点权重。
 * @param controlPoints 控制点。
 * @param weights 原始权重。
 * @returns 与控制点对齐的安全权重。
 */
function normalizeWeights(controlPoints: CurveSamplePoint3[], weights: number[]): number[] {
    return controlPoints.map((_point, index) => {
        const weight = weights[index];
        return Number.isFinite(weight) ? Math.max(1e-6, weight) : 1;
    });
}

/**
 * 采样 B 样条曲线。
 * @param controlPoints 控制点。
 * @param degree 曲线次数。
 * @param segments 采样段数。
 * @returns 曲线采样点。
 */
export function sampleBSplinePoints(
    controlPoints: CurveSamplePoint3[],
    degree: number,
    segments: number,
): CurveSamplePoint3[] {
    return sampleRationalBSplinePoints(controlPoints, [], degree, segments);
}

/**
 * 采样有理 B 样条曲线。
 * @param controlPoints 控制点。
 * @param weights 控制点权重。
 * @param degree 曲线次数。
 * @param segments 采样段数。
 * @returns 曲线采样点。
 */
export function sampleRationalBSplinePoints(
    controlPoints: CurveSamplePoint3[],
    weights: number[],
    degree: number,
    segments: number,
): CurveSamplePoint3[] {
    if (controlPoints.length < 2) {
        return controlPoints;
    }

    const resolvedDegree = Math.max(1, Math.min(Math.floor(degree), controlPoints.length - 1));
    const knots = buildClampedUniformKnots(controlPoints.length, resolvedDegree);
    const normalizedWeights = normalizeWeights(controlPoints, weights);
    const sampleCount = Math.max(1, segments);

    return Array.from({ length: sampleCount + 1 }, (_unused, index) => (
        evaluateRationalBSplinePoint(index / sampleCount, resolvedDegree, controlPoints, normalizedWeights, knots)
    ));
}

/**
 * 使用 De Casteljau 算法计算有理 Bezier 曲线点。
 * @param controlPoints 控制点。
 * @param weights 控制点权重。
 * @param parameter 曲线参数。
 * @returns 曲线点。
 */
function evaluateRationalBezierPoint(
    controlPoints: CurveSamplePoint3[],
    weights: number[],
    parameter: number,
): CurveSamplePoint3 {
    const workingPoints = controlPoints.map((point, index) => {
        const weight = Math.max(1e-6, weights[index] ?? 1);
        return {
            x: point.x * weight,
            y: point.y * weight,
            z: point.z * weight,
            w: weight,
        };
    });

    for (let step = 1; step < controlPoints.length; step += 1) {
        for (let index = 0; index < controlPoints.length - step; index += 1) {
            const left = workingPoints[index]!;
            const right = workingPoints[index + 1]!;
            workingPoints[index] = {
                x: (1 - parameter) * left.x + parameter * right.x,
                y: (1 - parameter) * left.y + parameter * right.y,
                z: (1 - parameter) * left.z + parameter * right.z,
                w: (1 - parameter) * left.w + parameter * right.w,
            };
        }
    }

    const result = workingPoints[0]!;
    return result.w === 0
        ? { x: result.x, y: result.y, z: result.z }
        : { x: result.x / result.w, y: result.y / result.w, z: result.z / result.w };
}

/**
 * 采样有理 Bezier 曲线。
 * @param controlPoints 控制点。
 * @param weights 控制点权重。
 * @param segments 采样段数。
 * @returns 曲线采样点。
 */
export function sampleRationalBezierPoints(
    controlPoints: CurveSamplePoint3[],
    weights: number[],
    segments: number,
): CurveSamplePoint3[] {
    if (controlPoints.length < 2) {
        return controlPoints;
    }

    const normalizedWeights = normalizeWeights(controlPoints, weights);
    const sampleCount = Math.max(1, segments);
    return Array.from({ length: sampleCount + 1 }, (_unused, index) => (
        evaluateRationalBezierPoint(controlPoints, normalizedWeights, index / sampleCount)
    ));
}
